/**
 * ToothChart — odontograma persistente del paciente (perfil dental).
 *
 * Dentadura completa (32 dientes, numeración FDI) donde se marca cada diente
 * con un estado (caries, obturado, endodoncia, corona, implante, ausente,
 * a extraer) y una nota por diente, más anotaciones generales.
 *
 * Se guarda en el propio paciente (`patient.dental_chart`) — el modelo acepta
 * campos extra, así que persiste con un PUT normal (mismo patrón que el mapa
 * facial de cirugía plástica).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, ChevronDown, ChevronUp, Eraser, Smile } from 'lucide-react';
import { api, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { SectionCard } from '../Common';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

// Estados posibles de un diente (código → etiqueta y color).
const ESTADOS = [
  { code: 'caries', label: 'Caries', color: '#e11d48' },
  { code: 'obturado', label: 'Obturado', color: '#2563eb' },
  { code: 'endodoncia', label: 'Endodoncia', color: '#f59e0b' },
  { code: 'corona', label: 'Corona', color: '#8b5cf6' },
  { code: 'implante', label: 'Implante', color: '#6b7280' },
  { code: 'ausente', label: 'Ausente', color: '#cbd5e1' },
  { code: 'extraer', label: 'A extraer', color: '#7a1f2b' },
];
const ESTADO_BY_CODE = Object.fromEntries(ESTADOS.map((e) => [e.code, e]));

// Numeración FDI: cuadrantes 1-2 arriba (derecha→izquierda del paciente),
// 4-3 abajo. Se pintan de izquierda a derecha tal y como MIRA el dentista.
const UPPER = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
const LOWER = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

function Tooth({ fdi, estado, onClick }) {
  const info = estado ? ESTADO_BY_CODE[estado] : null;
  const fill = info ? info.color : '#ffffff';
  const isAusente = estado === 'ausente';
  return (
    <g onClick={() => onClick(fdi)} className="cursor-pointer" data-testid={`tooth-${fdi}`}>
      <rect
        x={0} y={0} width={30} height={40} rx={9}
        fill={isAusente ? '#f8fafc' : fill}
        fillOpacity={info && !isAusente ? 0.85 : 1}
        stroke={info ? info.color : '#94a3b8'}
        strokeWidth={info ? 2 : 1.2}
      />
      {isAusente && (
        <>
          <line x1={6} y1={8} x2={24} y2={32} stroke="#94a3b8" strokeWidth={2.5} />
          <line x1={24} y1={8} x2={6} y2={32} stroke="#94a3b8" strokeWidth={2.5} />
        </>
      )}
    </g>
  );
}

export default function ToothChart({ patient, onPatientUpdated }) {
  const { user } = useAuth();
  const dc = patient.dental_chart || {};
  const [teeth, setTeeth] = useState(dc.teeth || {});
  const [notes, setNotes] = useState(dc.notes || '');
  const [estado, setEstado] = useState('caries');
  const [open, setOpen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const d = patient.dental_chart || {};
    setTeeth(d.teeth || {});
    setNotes(d.notes || '');
    setDirty(false);
  }, [patient.id, patient.dental_chart]);

  const meta = dc.updated_at
    ? `Última edición: ${formatDateTime(dc.updated_at)}${dc.updated_by_name ? ` · ${dc.updated_by_name}` : ''}`
    : null;

  const clickTooth = (fdi) => {
    const key = String(fdi);
    setTeeth((prev) => {
      const next = { ...prev };
      if (next[key]?.estado === estado) {
        delete next[key]; // segundo clic con el mismo estado → diente sano
      } else {
        next[key] = { ...(next[key] || {}), estado };
      }
      return next;
    });
    setDirty(true);
  };

  const setToothNote = (key, note) => {
    setTeeth((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), note } }));
    setDirty(true);
  };
  const removeTooth = (key) => {
    setTeeth((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setDirty(true);
  };
  const clearAll = () => {
    if (!Object.keys(teeth).length) return;
    if (!window.confirm('¿Borrar todas las marcas del odontograma?')) return;
    setTeeth({});
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/patients/${patient.id}`, {
        dental_chart: {
          teeth,
          notes,
          updated_at: new Date().toISOString(),
          updated_by_name: user?.name || '',
        },
      });
      toast.success('Odontograma guardado');
      setDirty(false);
      onPatientUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el odontograma');
    } finally {
      setSaving(false);
    }
  };

  const marked = useMemo(
    () => Object.entries(teeth).sort((a, b) => Number(a[0]) - Number(b[0])),
    [teeth],
  );

  const renderRow = (row, y, numbersAbove) => (
    <g transform={`translate(0 ${y})`}>
      {row.map((fdi, i) => {
        const x = 8 + i * 34 + (i >= 8 ? 14 : 0); // hueco entre cuadrantes
        return (
          <g key={fdi} transform={`translate(${x} 0)`}>
            <text
              x={15} y={numbersAbove ? -6 : 56}
              textAnchor="middle" fontSize="10" fill="#64748b" fontFamily="ui-sans-serif"
            >
              {fdi}
            </text>
            <Tooth fdi={fdi} estado={teeth[String(fdi)]?.estado} onClick={clickTooth} />
          </g>
        );
      })}
    </g>
  );

  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-2">
          <Smile className="w-4 h-4 text-[#2E6E5E]" /> Odontograma
          {marked.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground">({marked.length} dientes marcados)</span>
          )}
        </span>
      }
      action={
        <Button size="sm" variant="ghost" onClick={() => setOpen((o) => !o)} data-testid="tooth-chart-toggle">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {open ? 'Ocultar' : 'Abrir'}
        </Button>
      }
    >
      {!open ? (
        <p className="text-sm text-muted-foreground">
          Dentadura completa (FDI): marca cada diente con su estado y anota el tratamiento. Pulsa «Abrir» para editar.
        </p>
      ) : (
        <div className="space-y-3">
          {/* Paleta de estados */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground mr-1">Marcar como:</span>
            {ESTADOS.map((e) => (
              <button
                key={e.code}
                type="button"
                onClick={() => setEstado(e.code)}
                data-testid={`tooth-estado-${e.code}`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                  estado === e.code ? 'border-[#13231E] bg-[rgba(0,0,0,0.04)]' : 'border-[#D9E3DD]'
                }`}
              >
                <span className="w-3 h-3 rounded-full inline-block border border-black/10" style={{ backgroundColor: e.color }} />
                {e.label}
              </button>
            ))}
          </div>

          {/* Dentadura */}
          <div className="rounded-lg border border-[#D9E3DD] bg-white overflow-x-auto">
            <svg viewBox="0 0 570 160" className="w-full min-w-[540px] select-none" data-testid="tooth-chart-svg">
              {renderRow(UPPER, 18, true)}
              <line x1={10} y1={82} x2={560} y2={82} stroke="#e2e8f0" strokeWidth={1} />
              {renderRow(LOWER, 92, false)}
              {/* separador de cuadrantes */}
              <line x1={285} y1={6} x2={285} y2={154} stroke="#e2e8f0" strokeWidth={1} />
            </svg>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-muted-foreground">
              Clic en un diente para marcarlo (repite el clic para dejarlo sano)
            </span>
            {marked.length > 0 && (
              <button type="button" onClick={clearAll} className="inline-flex items-center gap-1 text-xs text-red-700 hover:text-red-800">
                <Eraser className="w-3.5 h-3.5" /> Limpiar todo
              </button>
            )}
          </div>

          {/* Dientes marcados con nota */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">
              Dientes marcados
            </div>
            {marked.length === 0 ? (
              <p className="text-sm text-muted-foreground">Ninguno todavía. Elige un estado y haz clic en un diente.</p>
            ) : (
              <ol className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {marked.map(([key, t]) => {
                  const info = ESTADO_BY_CODE[t.estado] || {};
                  return (
                    <li key={key} className="flex items-center gap-2">
                      <span
                        className="w-9 h-6 shrink-0 rounded-md inline-flex items-center justify-center text-[11px] font-bold text-white"
                        style={{ backgroundColor: info.color || '#64748b' }}
                        title={info.label}
                      >
                        {key}
                      </span>
                      <span className="text-xs text-muted-foreground w-20 shrink-0">{info.label}</span>
                      <input
                        type="text"
                        value={t.note || ''}
                        onChange={(e) => setToothNote(key, e.target.value)}
                        placeholder="Nota (ej.: obturación MOD, composite…)"
                        className="flex-1 h-7 rounded-md border border-input bg-transparent px-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        data-testid={`tooth-note-${key}`}
                      />
                      <button type="button" onClick={() => removeTooth(key)} className="text-muted-foreground hover:text-red-700" title="Quitar marca">
                        ✕
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          {/* Anotaciones generales + guardar */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">
              Anotaciones generales
            </div>
            <Textarea
              value={notes}
              onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
              rows={2}
              placeholder="Plan de tratamiento, observaciones de la boca…"
              className="text-xs"
              data-testid="tooth-chart-notes"
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px] text-muted-foreground">{meta}</span>
            <Button
              size="sm"
              onClick={save}
              disabled={saving || !dirty}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white"
              data-testid="tooth-chart-save"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Guardar odontograma
            </Button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}
